import Honeybadger from '@honeybadger-io/js'

// Report Content-Security-Policy violations to Honeybadger from the BROWSER.
//
// We don't forward from the server (server/middleware/05-security-headers.ts):
// @honeybadger-io/js's server-side reporting doesn't work in Nuxt, and the
// report-uri/report-to delivery to that endpoint is unreliable. The
// `securitypolicyviolation` DOM event, by contrast, fires reliably in-page for
// both Report-Only and enforced policies, and the browser Honeybadger instance
// (configured in plugins/01.honeybadger.ts) does work.
//
// Caveat: a page-script listener attaches only after the JS bundle loads, and
// these events aren't buffered — so it can't catch the parse-time __NUXT__
// bootstrap script violation, which fires before any JS runs. That one is
// already known; everything that violates at runtime is captured here.
export default defineNuxtPlugin(() => {
  if (typeof document === 'undefined') return

  // Don't report under `nuxi dev`. Vite's dev client and its pre-bundled
  // dependency cache (`node_modules/.cache/vite/**`) use `eval`, which trips
  // `script-src 'self' 'wasm-unsafe-eval'` — a violation that exists only in
  // dev and never in a production Rollup build. Reporting it just floods the
  // Report-Only stream we're trying to keep clean before enforcing (Phase 1).
  if (import.meta.dev) return

  // Dedupe within the session so a repeatedly-firing violation doesn't flood
  // Honeybadger with identical notices.
  const seen = new Set<string>()

  document.addEventListener(
    'securitypolicyviolation',
    (e: SecurityPolicyViolationEvent) => {
      const key = [
        e.effectiveDirective || e.violatedDirective,
        e.blockedURI,
        `${e.sourceFile}:${e.lineNumber}:${e.columnNumber}`,
        e.sample,
      ].join('|')
      if (seen.has(key)) return
      seen.add(key)

      Honeybadger.notify({
        name: 'CSPViolation',
        message: `CSP ${e.disposition}: ${e.violatedDirective} blocked ${
          e.blockedURI || '(inline)'
        }`,
        context: {
          violatedDirective: e.violatedDirective,
          effectiveDirective: e.effectiveDirective,
          blockedURI: e.blockedURI,
          documentURI: e.documentURI,
          sourceFile: e.sourceFile,
          lineNumber: e.lineNumber,
          columnNumber: e.columnNumber,
          sample: e.sample,
          disposition: e.disposition,
        },
      })
    }
  )
})
